import { SelectionModel } from '@angular/cdk/collections';
import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { normalizarValorOrdenacao } from '../../../shared/helpers/ordenacao-tabela.helper';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { LoadingBoxComponent } from '../../../shared/components/loading-box/loading-box.component';
import { EmptyListComponent } from '../../../shared/components/empty-list/empty-list.component';
import { ModuloEnum } from '../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../shared/enums/usuario/funcionalidade.enum';
import { GetAllVendedorResponse } from '../../../shared/services/responses/pessoa/get-all-vendedor.response';
import { Vendedor } from '../../../shared/services/models/pessoa/vendedor.model';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { VendedorService } from '../../../shared/services/apis/pessoa/vendedor.service';
import { ReturnStruct } from '../../../shared/services/structs/return.struct';
import { SituacaoVendedorEnum } from '../../../shared/enums/pessoa/situacao-vendedor.enum';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { UtilService } from '../../../shared/services/util.service';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { LoadingBoxDefaultListComponent } from '../../../shared/components/loading-box-default-list/loading-box-default-list.component';
import { VendedorStruct } from '../../../shared/services/structs/pessoa/vendedor.struct';
import { MunicipioService } from '../../../shared/services/apis/usuario/municipio.service';
import { VendedorHierarquiaDialogComponent } from './dialogs/vendedor-hierarquia-dialog/vendedor-hierarquia-dialog.component';
import { AlterarVendedorResponsavelMassaDialogComponent } from './dialogs/alterar-vendedor-responsavel-massa-dialog/alterar-vendedor-responsavel-massa-dialog.component';

@Component({
  selector: 'app-vendedor-lista',
  standalone: true,
  imports: [
    CommonModule,
    HeaderComponent,
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatCheckboxModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatButtonModule,
    MatMenuModule,
    EmptyListComponent,
    RouterModule,
    MatSortModule,
    LoadingBoxDefaultListComponent
  ],
  templateUrl: './vendedor-lista.component.html',
  styleUrl: './vendedor-lista.component.css'
})
export class VendedorListaComponent implements OnInit {

  // Enums para Menu e Permissões
  public moduloEnum = ModuloEnum.Cadastros; // Ajuste conforme seu ModuloEnum real
  public funcionalidadeEnum = FuncionalidadeEnum.Cadastros_Vendedores; // Ajuste para a funcionalidade Vendedor se existir

  public model: FormGroup;
  public _isLoading: boolean = false;

  // Dados e Paginação
  public _listaVendedorResponse: GetAllVendedorResponse | undefined;
  public _dataSource = new MatTableDataSource<VendedorStruct>();
  public _paginaAtual: number = 0;
  public _ultimaPagina: boolean = false;

  // Colunas do Grid
  public displayedColumns: string[] = [
    'select',
    'nome',
    'vendedorResponsavel',
    'cidade',
    'estado',
    'status',
    'actions'
  ];

  // Controle de Seleção (Checkboxes)
  public selection = new SelectionModel<VendedorStruct>(true, []);

  @ViewChild(MatSort) set matSort(sort: MatSort) {
    this._dataSource.sort = sort;
    this._dataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'nome': return normalizarValorOrdenacao(item.vendedor.nome);
        case 'status': return normalizarValorOrdenacao(item.vendedor.idSituacaoVendedor);
        case 'cidade': return normalizarValorOrdenacao(item.cidade);
        case 'estado': return normalizarValorOrdenacao(item.uf);
        // Story, seção 5.1: vendedores sem responsável sempre vão para o fim, nas duas direções.
        case 'vendedorResponsavel': return item.nomeVendedorResponsavel
          ? normalizarValorOrdenacao(item.nomeVendedorResponsavel)
          : (this._dataSource.sort?.direction === 'desc' ? '' : '￿');
        default: return normalizarValorOrdenacao((item as any)[property]);
      }
    };
  }

  constructor(
    private formBuilder: FormBuilder,
    private vendedorService: VendedorService,
    private municipioService: MunicipioService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private utilService: UtilService
  ) { }

  ngOnInit(): void {
    this.model = this.formBuilder.group({
      pesquisa: ['']
    });

    this.loadFilters();
    this.pesquisar();
  }

  ngOnDestroy(): void {
    this.utilService.salvarFiltro(this.moduloEnum, this.funcionalidadeEnum, this.model, ['/cadastros/vendedor/cadastrar']);
  }

  private loadFilters() {
    const filtroSalvo = this.utilService.obterFiltro(this.moduloEnum, this.funcionalidadeEnum);
    if (filtroSalvo) {
      this.model.patchValue(filtroSalvo);
    }
  }

  // --- Lógica de Pesquisa e Paginação ---

  public pesquisar(): void {
    if (this._isLoading) return;

    this._isLoading = true;
    const termoBusca: string = this.model.get('pesquisa')?.value;

    this.vendedorService.getAll(termoBusca, this._paginaAtual).subscribe({
      next: (response: GetAllVendedorResponse) => {
        if (response.isError) {
          this._isLoading = false;
          this.alertService.show('Erro', response.errorDescription, AlertType.error);
          return;
        }

        const ids = [...new Set(response.listVendedores
          .map(item => item.vendedor.idMunicipio)
          .filter((id): id is number => !!id))];

        if (!ids.length) {
          this._isLoading = false;
          if (!this._listaVendedorResponse) {
            this._listaVendedorResponse = response;
            this._dataSource.data = response.listVendedores;
          } else {
            this._listaVendedorResponse.listVendedores.push(...response.listVendedores);
            this._dataSource.data = this._listaVendedorResponse.listVendedores;
          }
          this._ultimaPagina = !response.listVendedores?.length ||
            this._dataSource.data.length >= response.fullListSize;
          return;
        }

        this.municipioService.getByIds(ids).subscribe({
          next: municipioResponse => {
            if (municipioResponse.isError) {
              this.alertService.show('Atenção', 'Não foi possível carregar cidade e UF dos vendedores.', AlertType.warning);
            } else {
              const municipios = new Map(municipioResponse.list.map(item => [item.idMunicipio, item]));
              response.listVendedores.forEach(item => {
                const municipio = item.vendedor.idMunicipio
                  ? municipios.get(item.vendedor.idMunicipio)
                  : undefined;
                item.cidade = municipio?.nome ?? '';
                item.uf = municipio?.uf ?? '';
              });
            }

            this._isLoading = false;
            if (!this._listaVendedorResponse) {
              this._listaVendedorResponse = response;
              this._dataSource.data = response.listVendedores;
            } else {
              this._listaVendedorResponse.listVendedores.push(...response.listVendedores);
              this._dataSource.data = this._listaVendedorResponse.listVendedores;
            }
            this._ultimaPagina = !response.listVendedores?.length ||
              this._dataSource.data.length >= response.fullListSize;
          },
          error: () => {
            this.alertService.show('Atenção', 'Não foi possível carregar cidade e UF dos vendedores.', AlertType.warning);
            this._isLoading = false;
            if (!this._listaVendedorResponse) {
              this._listaVendedorResponse = response;
              this._dataSource.data = response.listVendedores;
            } else {
              this._listaVendedorResponse.listVendedores.push(...response.listVendedores);
              this._dataSource.data = this._listaVendedorResponse.listVendedores;
            }
            this._ultimaPagina = !response.listVendedores?.length ||
              this._dataSource.data.length >= response.fullListSize;
          }
        });
      },
      error: (err) => {
        this._isLoading = false;
        this.alertService.show('Erro inesperado', err.message || err, AlertType.error);
      }
    });
  }

  public clickPesquisar(): void {
    this._paginaAtual = 0;
    this._listaVendedorResponse = undefined;
    this._dataSource.data = [];
    this._ultimaPagina = false;
    this.selection.clear(); // Limpa seleção ao pesquisar
    this.pesquisar();
  }

  public proximaPagina(): void {
    this._paginaAtual++;
    this.pesquisar();
  }

  // --- Lógica de Seleção (Checkbox) ---

  /** Se o número de selecionados for igual ao total de linhas */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this._dataSource.data.length;
    return numSelected === numRows && numRows > 0;
  }

  /** Seleciona tudo ou limpa seleção */
  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }
    this.selection.select(...this._dataSource.data);
  }

  /** Label para acessibilidade do checkbox */
  checkboxLabel(row?: VendedorStruct): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.vendedor.idVendedor}`;
  }

  // --- Ações (Excluir) ---

  public abrirModalExcluir(vendedorStruct?: VendedorStruct): void {
    const isLote = !vendedorStruct;
    const listaParaExcluir = isLote ? this.selection.selected : [vendedorStruct!];

    if (listaParaExcluir.length === 0) {
      this.alertService.show('Atenção', 'Nenhum vendedor selecionado.', AlertType.warning);
      return;
    }

    const descricao = isLote
      ? `Confirma a exclusão dos ${listaParaExcluir.length} vendedores selecionados?`
      : `Deseja excluir o vendedor ${vendedorStruct?.vendedor.nome}?`;

    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Confirmação de Exclusão',
        description: descricao,
        buttonConfirm: 'Confirmar',
        buttonClose: 'Cancelar',
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.confirm) {
        this.processarExclusao(listaParaExcluir);
      }
    });
  }

  public getTooltipAlterarResponsavelSemSelecao(): string {
    return this.selection.hasValue() ? '' : 'Selecione ao menos um vendedor na lista para alterar o vendedor responsável em lote.';
  }

  public getTooltipExcluirSemSelecao(): string {
    return this.selection.hasValue() ? '' : 'Selecione ao menos um vendedor na lista para excluir em lote.';
  }

  // --- Hierarquia (Story, seção 6) ---

  public abrirHierarquia(idVendedor: number): void {
    this.dialog.open(VendedorHierarquiaDialogComponent, {
      width: '520px',
      data: { idVendedor }
    });
  }

  // --- Ação em massa: Alterar vendedor responsável (Story, seção 7) ---

  public abrirModalAlterarResponsavelEmMassa(): void {
    if (!this.selection.hasValue()) return;

    const dialogRef = this.dialog.open(AlterarVendedorResponsavelMassaDialogComponent, {
      width: '560px',
      data: {
        idsVendedores: this.selection.selected.map(s => s.vendedor.idVendedor),
        nomesVendedores: this.selection.selected.map(s => s.vendedor.nome),
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.success) {
        this.selection.clear();
        this.clickPesquisar();
      }
    });
  }

  private processarExclusao(lista: VendedorStruct[]): void {
    this._isLoading = true;
    const total = lista.length;
    const listIdVendedor: number[] = lista.map(x => x.vendedor.idVendedor);
    this.vendedorService.excluirMultiplos(listIdVendedor).subscribe({
      next: (res: ReturnStruct) => {
        this._isLoading = false;
        if (res.isError) {
          this.alertService.show('Alerta', res.errorDescription, AlertType.warning);
          return;
        }
        this.alertService.show('Sucesso', `${total} vendedor(es) excluído(s).`, AlertType.success);
        this.clickPesquisar()
      },
      error: (err: any) => {
        console.log(err)
      }
    });
  }

  // --- Helpers de Template ---

  /** * Mapeia ID de situação para Texto e Classe CSS 
   * 1 = Ativo, 2 = Inativo (Exemplo hipotético, ajustar conforme domínio real)
   */
  getStatusLabel(idSituacao: number): string {
    // Ajustar conforme IDs reais do sistema
    switch (idSituacao) {
      case SituacaoVendedorEnum.Ativo: return 'Ativo'; // Exemplo
      case SituacaoVendedorEnum.Inativo: return 'Inativo';
      case SituacaoVendedorEnum.AtivoSemAcessoSistema: return 'Ativo s/ Acesso';
      default: return 'Desconhecido';
    }
  }

  getStatusClass(idSituacao: number): string {
    switch (idSituacao) {
      case SituacaoVendedorEnum.Ativo:
        return 'badge-verde'; // Verde
      case SituacaoVendedorEnum.Inativo:
        return 'badge-cinza'; // Cinza
      case SituacaoVendedorEnum.AtivoSemAcessoSistema:
        return 'badge-amarelo'; // Verde
      default: return '';
    }
  }
}
